import * as THREE from 'three/webgpu';
import {
    FeatureResultMesh,
    isFeatureLine,
    isPlanarFeatureFace,
} from '../../mesh/feature';
import {isSketchLineObject} from '../../mesh/guards';
import {getDatumPlaneIdFromObject} from '../../reference/DatumPlane';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import {SketchEntityReferenceSelectionCandidateResolver}
    from '../../reference/sketch-entity-reference/command/selection/SketchEntityReferenceSelectionCandidateResolver';
import {createSketchEntityReferenceId}
    from '../../reference/sketch-entity-reference/model/SketchEntityReferenceIds';
import {SketchReferenceSourceResolver}
    from '../../reference/sketch-entity-reference/source/projection/SketchReferenceSourceResolver';
import type {SketchReferenceSourceResolverRuntime}
    from '../../reference/sketch-entity-reference/source/projection/SketchReferenceSourceResolver';
import {SceneTopologyOwnerRuntimeResolver}
    from '../../reference/sketch-entity-reference/source/topology/SceneTopologyOwnerRuntimeResolver';
import {TopologyFaceRelocationService}
    from '../../reference/sketch-entity-reference/source/topology/TopologyFaceRelocationService';
import type {TopologyOwnerRuntimeHost}
    from '../../../types/reference/sketch-entity-reference/TopologyOwnerRuntimeResolver';
import type {TopologyFaceReferenceSourceDescriptor}
    from '../../../types/reference/sketch-entity-reference/TopologyFaceReferenceSource';
import type {Viewer} from '../../viewer/Viewer';
import type {
    SketchMirrorAxisReference,
    SketchMirrorAxisReferenceResolverPort,
    SketchMirrorAxisResolvedGeometry,
    SketchMirrorAxisSelectionInput,
    SketchMirrorAxisSelectionResolution,
} from '../../../types/sketch/mirror/SketchMirrorAxisReferenceTypes';
import {SketchMirrorAxisReferenceProvider} from './SketchMirrorAxisReferenceProvider';

/** 二维镜像轴判定使用的世界坐标公差。 */
const MIRROR_AXIS_WORLD_TOLERANCE = 1e-6;
/** 平面法线平行判定使用的平方公差。 */
const MIRROR_AXIS_PARALLEL_TOLERANCE_SQUARED = 1e-12;

/**
 * 镜像轴稳定引用解析器。
 *
 * 外部边和面完全复用 Convert Entities 的 owner、occurrence 与拓扑重定位协议。
 * 本类只负责把当前重定位结果解释为活动草图平面中的唯一二维直线，不另建
 * 第二套模型引用身份，也不持有会在重建后失效的 FeatureLine/FeatureFace 代理。
 */
export class SketchMirrorAxisReferenceResolver implements SketchMirrorAxisReferenceResolverPort {
    /** 复用 Convert Entities 的选择 descriptor 生成规则。 */
    private readonly selectionCandidateResolver = new SketchEntityReferenceSelectionCandidateResolver();
    /** 复用 Convert Entities 的拓扑边重定位。 */
    private readonly sourceResolver = new SketchReferenceSourceResolver();
    /** 复用 Convert Entities 的拓扑面谱系重定位。 */
    private readonly faceRelocationService = new TopologyFaceRelocationService();
    /** 从当前场景恢复 topology owner，不缓存结果体对象。 */
    private readonly topologyOwnerResolver: SceneTopologyOwnerRuntimeResolver;
    /** 拓扑直边解析使用的运行时端口。 */
    private readonly topologyEdgeRuntime: SketchReferenceSourceResolverRuntime;
    /** 平面面求交复用平面。 */
    private readonly facePlaneScratch = new THREE.Plane();
    /** 基准面求交复用平面。 */
    private readonly datumPlaneScratch = new THREE.Plane();
    /** 平面求交复用法线。 */
    private readonly sourceNormalScratch = new THREE.Vector3();
    /** 平面求交复用目标法线。 */
    private readonly targetNormalScratch = new THREE.Vector3();
    /** 平面求交复用方向。 */
    private readonly directionScratch = new THREE.Vector3();
    /** 平面求交复用交线上一点。 */
    private readonly pointOnLineScratch = new THREE.Vector3();
    /** 多后代平面交线一致性检查使用的起点。 */
    private readonly candidateStartScratch = new THREE.Vector3();
    /** 多后代平面交线一致性检查使用的终点。 */
    private readonly candidateEndScratch = new THREE.Vector3();
    /** 多后代平面交线一致性检查使用的方向。 */
    private readonly candidateDirectionScratch = new THREE.Vector3();
    /** 多后代平面交线一致性检查使用的偏移。 */
    private readonly candidateOffsetScratch = new THREE.Vector3();

    /**
     * @param viewer 当前 CAD Viewer。
     */
    public constructor(private readonly viewer: Viewer) {
        this.topologyOwnerResolver = new SceneTopologyOwnerRuntimeResolver({
            sceneRoot: viewer.scene,
            findSceneObjectById: objectId => viewer.findSceneObjectById(objectId),
        });
        this.topologyEdgeRuntime = {
            getSketchObject: () => null,
            getFeatureLine: () => null,
            resolveTopologyOwnerHost: descriptor => this.topologyOwnerResolver.resolve(descriptor),
        };
    }

    /**
     * 解析视口选择为统一镜像轴目标。
     * @param input 当前命中对象与活动草图。
     * @returns 合法选择；来源不受支持或不满足二维规则时返回 null。
     */
    public resolveSelection(input: SketchMirrorAxisSelectionInput): SketchMirrorAxisSelectionResolution | null {
        const sketchLine = this.resolveActiveSketchLine(input);
        if (sketchLine) {
            return {
                provider: sketchLine,
                sourceObject: sketchLine,
                isExternalReference: false,
            };
        }

        const reference = this.createExternalReference(input);
        if (!reference) {
            return null;
        }
        const provider = new SketchMirrorAxisReferenceProvider({
            reference,
            referencePlane: input.sketchGroup.referencePlane,
            resolver: this,
        });
        // 选择时就拒绝悬空或无唯一交线的来源，避免非法状态进入命令。
        if (!provider.resolveMirrorAxisGeometry()) {
            provider.dispose();
            return null;
        }
        return {
            provider,
            sourceObject: input.object,
            isExternalReference: true,
        };
    }

    /**
     * 按稳定引用解析当前世界轴线。
     * @param reference 稳定外部引用。
     * @param targetPlane 目标草图平面。
     * @param output 可复用的输出向量。
     * @returns 合法二维轴线；来源悬空或不再满足规则时返回 null。
     */
    public resolveMirrorAxisReference(
        reference: SketchMirrorAxisReference,
        targetPlane: ReferencePlane,
        output?: SketchMirrorAxisResolvedGeometry,
    ): SketchMirrorAxisResolvedGeometry | null {
        const resolvedOutput = output ?? {
            startWorld: new THREE.Vector3(),
            endWorld: new THREE.Vector3(),
        };
        if (reference.source.kind === 'topology-edge') {
            return this.resolveTopologyEdge(reference, targetPlane, resolvedOutput);
        }
        if (reference.source.kind === 'datum-plane') {
            const datumPlane = this.viewer.getDatumPlane(reference.source.datumPlaneId);
            if (!datumPlane) {
                return null;
            }
            this.datumPlaneScratch.normal.copy(datumPlane.normal);
            this.datumPlaneScratch.constant = datumPlane.constant;
            return this.resolvePlaneIntersection(this.datumPlaneScratch, targetPlane, resolvedOutput);
        }
        return this.resolvePlanarFace(reference.source.face, targetPlane, resolvedOutput);
    }

    /**
     * 从当前活动草图内解析普通、中心或构造直线。
     * @param input 当前选择输入。
     * @returns 非退化活动草图直线；否则返回 null。
     */
    private resolveActiveSketchLine(input: SketchMirrorAxisSelectionInput) {
        if (!isSketchLineObject(input.object)) {
            return null;
        }
        const currentObject = input.sketchGroup.getObjectByProperty('uuid', input.object.uuid);
        return currentObject === input.object && input.object.resolveMirrorAxisGeometry()
            ? input.object
            : null;
    }

    /**
     * 从外部命中对象创建稳定引用。
     * @param input 当前选择输入。
     * @returns 稳定引用；对象不支持或身份不足时返回 null。
     */
    private createExternalReference(input: SketchMirrorAxisSelectionInput): SketchMirrorAxisReference | null {
        const datumPlaneId = getDatumPlaneIdFromObject(input.object);
        if (datumPlaneId !== null) {
            return {
                schemaVersion: 1,
                referenceId: createSketchEntityReferenceId(),
                source: {kind: 'datum-plane', datumPlaneId},
            };
        }

        const candidate = this.selectionCandidateResolver.resolve({
            targetObject: input.object,
            targetSketchId: input.sketchGroup.uuid,
        });
        if (isFeatureLine(input.object)) {
            if (
                candidate?.descriptor.kind !== 'topology-edge'
                || candidate.descriptor.locator.curveKind !== 'line'
                || candidate.descriptor.locator.resolutionLevel !== 'exact'
            ) {
                return null;
            }
            const descriptor = structuredClone(candidate.descriptor);
            // 几何坐标只描述创建时快照，不能覆盖 occurrence/topology 的权威身份。
            delete descriptor.locator.geomFingerprint;
            return {
                schemaVersion: 1,
                referenceId: createSketchEntityReferenceId(),
                source: {kind: 'topology-edge', descriptor},
            };
        }

        if (isPlanarFeatureFace(input.object) && candidate?.intent?.topologyFaceSource) {
            return {
                schemaVersion: 1,
                referenceId: createSketchEntityReferenceId(),
                source: {
                    kind: 'planar-face',
                    face: structuredClone(candidate.intent.topologyFaceSource),
                },
            };
        }
        return null;
    }

    /**
     * 通过 Convert Entities 的来源解析器恢复当前线性模型边。
     * @param reference 稳定镜像轴引用。
     * @param targetPlane 目标草图平面。
     * @param output 复用输出。
     * @returns 共面且非退化的当前边；否则返回 null。
     */
    private resolveTopologyEdge(
        reference: SketchMirrorAxisReference,
        targetPlane: ReferencePlane,
        output: SketchMirrorAxisResolvedGeometry,
    ): SketchMirrorAxisResolvedGeometry | null {
        if (reference.source.kind !== 'topology-edge') {
            return null;
        }
        if (!this.sourceResolver.resolveExactTopologyLineTo(
            reference.source.descriptor,
            this.topologyEdgeRuntime,
            output.startWorld,
            output.endWorld,
        )) {
            return null;
        }
        return this.isNonDegenerateLine(output.startWorld, output.endWorld)
            && this.isPointOnPlane(output.startWorld, targetPlane)
            && this.isPointOnPlane(output.endWorld, targetPlane)
            ? output
            : null;
    }

    /**
     * 通过统一 owner 与面谱系重定位恢复当前平面面交线。
     *
     * 一个语义面经布尔操作后可能对应多个物理后代。只有所有后代都给出同一条
     * 无限交线时才保留引用；否则该来源对二维镜像轴而言已经不再唯一。
     *
     * @param descriptor 持久化面描述。
     * @param targetPlane 目标草图平面。
     * @param output 复用输出。
     * @returns 唯一面交线；悬空、歧义或无唯一交线时返回 null。
     */
    private resolvePlanarFace(
        descriptor: TopologyFaceReferenceSourceDescriptor,
        targetPlane: ReferencePlane,
        output: SketchMirrorAxisResolvedGeometry,
    ): SketchMirrorAxisResolvedGeometry | null {
        const ownerHost = this.resolveFaceOwnerHost(descriptor);
        if (!ownerHost?.listFaces) {
            return null;
        }
        const result = this.faceRelocationService.relocate(descriptor, {
            getFaceByIndex: faceIndex => ownerHost.getFaceByIndex?.(faceIndex) ?? null,
            listFaces: () => ownerHost.listFaces?.() ?? [],
        });
        if (result.status !== 'resolved' || result.faces.length <= 0) {
            return null;
        }

        let hasResolvedLine = false;
        for (const faceCandidate of result.faces) {
            const ownerMesh = this.resolveFaceCandidateOwnerMesh(descriptor, faceCandidate.topologyMemberId);
            const featureFace = ownerMesh?.getFeatureFaceByIndex(faceCandidate.faceIndex) ?? null;
            if (!featureFace || !isPlanarFeatureFace(featureFace)) {
                return null;
            }
            if (!featureFace.copyWorldPlaneTo(this.sourceNormalScratch, this.pointOnLineScratch)) {
                return null;
            }
            this.facePlaneScratch.setFromNormalAndCoplanarPoint(
                this.sourceNormalScratch,
                this.pointOnLineScratch,
            );
            const candidateOutput = hasResolvedLine
                ? {
                    startWorld: this.candidateStartScratch,
                    endWorld: this.candidateEndScratch,
                }
                : output;
            if (!this.resolvePlaneIntersection(this.facePlaneScratch, targetPlane, candidateOutput)) {
                return null;
            }
            if (hasResolvedLine && !this.areSameInfiniteLine(output, candidateOutput)) {
                return null;
            }
            hasResolvedLine = true;
        }
        return hasResolvedLine ? output : null;
    }

    /**
     * 为面 descriptor 构造一个边 descriptor，以复用统一 owner resolver。
     *
     * owner resolver 只读取 owner；locator 的占位边索引不会参与 Feature 结果体解析。
     * 导入体解析需要从当前边找到承载结果体，因此优先使用面边界中的第一条边索引。
     *
     * @param descriptor 面来源描述。
     * @returns 当前 topology owner host。
     */
    private resolveFaceOwnerHost(
        descriptor: TopologyFaceReferenceSourceDescriptor,
    ): TopologyOwnerRuntimeHost | null {
        const ownerMesh = this.resolveFeatureResultMesh(descriptor.owner.resultObjectId)
            ?? this.resolveFeatureResultMesh(descriptor.owner.bodyBindingId)
            ?? this.resolveFeatureResultMesh(descriptor.owner.ownerId);
        const faceSnapshot = ownerMesh?.getTopologyFaceSnapshotByIndex(descriptor.locator.faceIndex);
        const edgeIndex = faceSnapshot?.adjacentEdgeIndices[0] ?? 0;
        return this.topologyOwnerResolver.resolve({
            kind: 'topology-edge',
            owner: structuredClone(descriptor.owner),
            locator: {
                edgeIndex,
                curveKind: 'other',
                resolutionLevel: 'unsupported',
            },
        });
    }

    /**
     * 把面重定位候选映射回当前结果体。
     * @param descriptor 面来源描述。
     * @param topologyMemberId 重定位 host 提供的结果成员 ID。
     * @returns 当前 FeatureResultMesh；找不到时返回 null。
     */
    private resolveFaceCandidateOwnerMesh(
        descriptor: TopologyFaceReferenceSourceDescriptor,
        topologyMemberId?: string,
    ): FeatureResultMesh | null {
        return this.resolveFeatureResultMesh(topologyMemberId)
            ?? this.resolveFeatureResultMesh(descriptor.owner.resultObjectId)
            ?? this.resolveFeatureResultMesh(descriptor.owner.bodyBindingId)
            ?? this.resolveFeatureResultMesh(descriptor.owner.ownerId);
    }

    /**
     * 按稳定场景 ID 读取结果体，必要时向上查找承载拓扑的祖先。
     * @param objectId 结果成员或 owner ID。
     * @returns 可提供 FeatureFace 的结果体。
     */
    private resolveFeatureResultMesh(objectId?: string): FeatureResultMesh | null {
        if (!objectId) {
            return null;
        }
        let object: THREE.Object3D | null = this.viewer.findSceneObjectById(objectId);
        while (object) {
            if (object instanceof FeatureResultMesh) {
                return object;
            }
            object = object.parent;
        }
        return null;
    }

    /**
     * 判断两段非退化线是否表达同一条无限直线。
     * @param left 第一条线。
     * @param right 第二条线。
     * @returns 方向平行且相互距离在公差内时返回 true。
     */
    private areSameInfiniteLine(
        left: SketchMirrorAxisResolvedGeometry,
        right: SketchMirrorAxisResolvedGeometry,
    ): boolean {
        this.directionScratch.subVectors(left.endWorld, left.startWorld).normalize();
        this.candidateDirectionScratch.subVectors(right.endWorld, right.startWorld).normalize();
        if (
            this.candidateOffsetScratch
                .crossVectors(this.directionScratch, this.candidateDirectionScratch)
                .lengthSq() > MIRROR_AXIS_PARALLEL_TOLERANCE_SQUARED
        ) {
            return false;
        }
        return this.candidateOffsetScratch
            .subVectors(right.startWorld, left.startWorld)
            .cross(this.directionScratch)
            .lengthSq() <= MIRROR_AXIS_WORLD_TOLERANCE ** 2;
    }

    /**
     * 计算两个平面的唯一交线。
     * @param sourcePlane 外部基准面或模型面。
     * @param targetPlane 活动草图平面。
     * @param output 复用输出。
     * @returns 唯一交线；平行或重合时返回 null。
     */
    private resolvePlaneIntersection(
        sourcePlane: THREE.Plane,
        targetPlane: ReferencePlane,
        output: SketchMirrorAxisResolvedGeometry,
    ): SketchMirrorAxisResolvedGeometry | null {
        const sourceNormal = this.sourceNormalScratch.copy(sourcePlane.normal).normalize();
        const targetNormal = this.targetNormalScratch.copy(targetPlane.normal).normalize();
        const direction = this.directionScratch.crossVectors(sourceNormal, targetNormal);
        const denominator = direction.lengthSq();
        if (denominator <= MIRROR_AXIS_PARALLEL_TOLERANCE_SQUARED) {
            // 平行与重合都没有唯一交线，不能用任意方向伪造镜像轴。
            return null;
        }

        const pointOnLine = this.pointOnLineScratch.copy(sourceNormal)
            .multiplyScalar(targetPlane.constant)
            .addScaledVector(targetNormal, -sourcePlane.constant)
            .cross(direction)
            .divideScalar(denominator);
        direction.normalize();
        output.startWorld.copy(pointOnLine).addScaledVector(direction, -1);
        output.endWorld.copy(pointOnLine).add(direction);
        return output;
    }

    /**
     * 判断点是否位于活动草图平面。
     * @param point 世界坐标点。
     * @param plane 活动草图平面。
     * @returns 距离不超过统一公差返回 true。
     */
    private isPointOnPlane(point: THREE.Vector3, plane: ReferencePlane): boolean {
        return Math.abs(plane.distanceToPoint(point)) <= MIRROR_AXIS_WORLD_TOLERANCE;
    }

    /**
     * 判断直线端点是否非退化。
     * @param startWorld 起点。
     * @param endWorld 终点。
     * @returns 长度大于统一公差返回 true。
     */
    private isNonDegenerateLine(startWorld: THREE.Vector3, endWorld: THREE.Vector3): boolean {
        return startWorld.distanceToSquared(endWorld) > MIRROR_AXIS_WORLD_TOLERANCE ** 2;
    }
}
